// Validation utility functions for form inputs
import React from 'react';

export const validationRules = {
  // Email validation
  email: {
    required: (value) => value?.trim() !== '',
    pattern: (value) => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(value);
    },
    messages: {
      required: 'Email is required',
      pattern: 'Please enter a valid email address with proper domain (e.g., user@example.com)'
    }
  },

  // Phone validation
  phone: {
    required: (value) => {
      const isValid = value?.trim() !== '';
      return isValid;
    },
    pattern: (value) => {
      if (!value) return false;
      const phoneRegex = /^[6-9]\d{9}$/; // Indian mobile number format
      const cleanValue = value.replace(/\D/g, ''); // Remove all non-digits
      const isValid = phoneRegex.test(cleanValue);
      return isValid;
    },
    length: (value) => {
      if (!value) return false;
      const cleanPhone = value.replace(/\D/g, ''); // Remove all non-digits
      const isValid = cleanPhone.length === 10;
      return isValid;
    },
    messages: {
      required: 'Phone number is required',
      pattern: 'Please enter a valid 10-digit mobile number starting with 6-9',
      length: 'Phone number must be exactly 10 digits'
    }
  },

  // Name validation
  name: {
    required: (value) => value?.trim() !== '',
    minLength: (value) => value?.trim().length >= 2,
    maxLength: (value) => value?.trim().length <= 50,
    pattern: (value) => {
      const nameRegex = /^[a-zA-Z\s.'-]+$/;
      return nameRegex.test(value?.trim());
    },
    messages: {
      required: 'Name is required',
      minLength: 'Name must be at least 2 characters long',
      maxLength: 'Name cannot exceed 50 characters',
      pattern: 'Name can only contain letters, spaces, dots, hyphens, and apostrophes'
    }
  },

  // Address validation
  address: {
    minLength: (value) => {
      if (!value || value.trim() === '') return true; // Optional field
      return value.trim().length >= 10;
    },
    maxLength: (value) => {
      if (!value || value.trim() === '') return true; // Optional field
      return value.trim().length <= 200;
    },
    messages: {
      minLength: 'Address must be at least 10 characters long',
      maxLength: 'Address cannot exceed 200 characters'
    }
  },

  // Price/Amount validation
  price: {
    required: (value) => value !== '' && value !== null && value !== undefined,
    min: (value) => parseFloat(value) >= 0,
    max: (value) => parseFloat(value) <= 10000000, // 1 crore max
    pattern: (value) => {
      const priceRegex = /^\d+(\.\d{1,2})?$/;
      return priceRegex.test(value.toString());
    },
    messages: {
      required: 'Price is required',
      min: 'Price cannot be negative',
      max: 'Price cannot exceed ₹1,00,00,000',
      pattern: 'Please enter a valid price (up to 2 decimal places)'
    }
  },

  // Quantity validation
  quantity: {
    required: (value) => value !== '' && value !== null && value !== undefined,
    min: (value) => parseInt(value) >= 1,
    max: (value) => parseInt(value) <= 10000,
    pattern: (value) => {
      const quantityRegex = /^\d+$/;
      return quantityRegex.test(value.toString());
    },
    messages: {
      required: 'Quantity is required',
      min: 'Quantity must be at least 1',
      max: 'Quantity cannot exceed 10,000',
      pattern: 'Quantity must be a whole number'
    }
  },

  // Percentage validation (tax, discount)
  percentage: {
    min: (value) => {
      if (!value || value === '') return true; // Optional
      return parseFloat(value) >= 0;
    },
    max: (value) => {
      if (!value || value === '') return true; // Optional
      return parseFloat(value) <= 100;
    },
    pattern: (value) => {
      if (!value || value === '') return true; // Optional
      const percentageRegex = /^\d+(\.\d{1,2})?$/;
      return percentageRegex.test(value.toString());
    },
    messages: {
      min: 'Percentage cannot be negative',
      max: 'Percentage cannot exceed 100%',
      pattern: 'Please enter a valid percentage (up to 2 decimal places)'
    }
  },

  // Description validation
  description: {
    maxLength: (value) => {
      if (!value || value.trim() === '') return true; // Optional field
      return value.trim().length <= 500;
    },
    messages: {
      maxLength: 'Description cannot exceed 500 characters'
    }
  },

  // Required field validation
  required: {
    required: (value) => {
      if (typeof value === 'string') return value.trim() !== '';
      return value !== null && value !== undefined && value !== '';
    },
    messages: {
      required: 'This field is required'
    }
  },

  // Optional field validation
  optional: {
    required: (value) => true,
    messages: {}
  }
};

// Main validation function
export const validateField = (value, fieldType, customRules = {}) => {
  const rules = { ...validationRules[fieldType], ...customRules };
  const errors = [];

  if (!rules) return errors;

  // Check each rule
  Object.keys(rules).forEach(ruleName => {
    if (ruleName === 'messages') return;

    const ruleFunction = rules[ruleName];
    if (typeof ruleFunction === 'function' && !ruleFunction(value)) {
      const message = rules.messages?.[ruleName] || `${ruleName} validation failed`;
      errors.push(message);
    }
  });

  return errors;
};

// Validate entire form
export const validateForm = (formData, validationSchema) => {
  console.log('🔍 Form validation started:', { formData, validationSchema });
  const errors = {};
  let isValid = true;

  Object.keys(validationSchema).forEach(fieldName => {
    const fieldValue = formData[fieldName];
    const fieldRules = validationSchema[fieldName];
    
    console.log(`🔍 Validating field '${fieldName}':`, { 
      value: fieldValue, 
      rules: fieldRules 
    });
    
    const fieldErrors = validateField(fieldValue, fieldRules.type, fieldRules.customRules);
    
    console.log(`🔍 Field '${fieldName}' validation result:`, { 
      errors: fieldErrors, 
      hasErrors: fieldErrors.length > 0 
    });
    
    if (fieldErrors.length > 0) {
      errors[fieldName] = fieldErrors;
      isValid = false;
    }
  });

  console.log('🔍 Form validation completed:', { 
    isValid, 
    errors, 
    errorCount: Object.keys(errors).length 
  });

  return { isValid, errors };
};

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{5})(\d{5})/, '$1 $2');
  }
  return phone;
};

// Format GST number for display
export const formatGSTNumber = (gst) => {
  if (!gst) return '';
  const cleaned = gst.replace(/\s/g, '').toUpperCase();
  if (cleaned.length === 15) {
    return cleaned.replace(/(\d{2})([A-Z]{5})(\d{4})([A-Z]{1})([1-9A-Z]{1})(Z)([0-9A-Z]{1})/, '$1 $2 $3 $4$5$6$7');
  }
  return gst.toUpperCase();
};

// Real-time validation hook
export const useFieldValidation = (initialValue = '', fieldType, customRules = {}) => {
  const [value, setValue] = React.useState(initialValue);
  const [errors, setErrors] = React.useState([]);
  const [touched, setTouched] = React.useState(false);

  const validate = (newValue) => {
    const fieldErrors = validateField(newValue, fieldType, customRules);
    setErrors(fieldErrors);
    return fieldErrors.length === 0;
  };

  const handleChange = (newValue) => {
    setValue(newValue);
    if (touched) {
      validate(newValue);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    validate(value);
  };

  return {
    value,
    errors,
    touched,
    isValid: errors.length === 0,
    handleChange,
    handleBlur,
    validate: () => validate(value)
  };
};
